import {
  exportPrisma,
  prisma,
  reportingPrisma,
  OrderItem,
  CheckInOutType,
  OrderStatus,
  OrderItemStatus,
  TransitionState,
  Prisma,
} from "client";
import { transformSurveyResult } from "../formTemplates";
import { getZoneByAgentId } from "services/zone";
import { isMongoObjectId, normalizeScanQuery } from "@sajilni/utils/ticketQr";
import {
  buildLatestCheckInOutLookup,
  latestCheckInOutStatusExpression,
} from "./latest-checkinout.pure";
export enum REPORT_TYPE {
  WHITE_LABEL = "whiteLabel",
  EVENT = "event",
  ORGANIZATION = "organization",
}
const NOT_CHECKED_IN = "NOT_CHECKED_IN";
export enum REPORT_GROUP_BY {
  EVENT_DATE = "event_date",
  TICKET_TYPE = "ticket_type",
}

type AttendeeReportAggregationRawResponse = {
  result?: Record<string, unknown>[];
  stats?: Array<{
    total?: number;
    checkedIn?: number;
    checkedOut?: number;
    notCheckedIn?: number;
  }>;
  pagination?: Array<{
    total?: number;
    limit?: number;
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
  }>;
};

type MongoPipelineStage = Prisma.InputJsonObject;

const translationLookupPipeline = (languageOid: { $oid: string } | null) =>
  languageOid
    ? [
        { $match: { languageId: languageOid } },
        { $project: { _id: 0, name: 1, description: 1 } },
      ]
    : [{ $project: { _id: 0, name: 1, description: 1 } }];

type OrderItemWithReportRelations = OrderItem & {
  formResults: unknown[];
  order: Record<string, unknown> & {
    formResults?: unknown[];
  };
};

/**
 * Indexed Order-side scope for attendee reports.
 * Prefer matching `{ eventId, status: COMPLETED }` over fetching all order IDs
 * into Node and querying OrderItem with `$in`.
 */
const resolveCompletedOrderScopeMatch = async (
  entityId: string,
  type: REPORT_TYPE,
  db: typeof prisma = prisma
): Promise<MongoPipelineStage | null> => {
  if (type === REPORT_TYPE.EVENT) {
    return {
      $match: {
        eventId: { $oid: entityId },
        status: OrderStatus.COMPLETED,
      },
    };
  }

  if (type === REPORT_TYPE.WHITE_LABEL) {
    const events = await db.event.findMany({
      where: { whiteLabelId: entityId },
      select: { id: true },
    });
    if (!events.length) {
      return null;
    }
    return {
      $match: {
        eventId: { $in: events.map((e) => ({ $oid: e.id })) },
        status: OrderStatus.COMPLETED,
      },
    };
  }

  if (type === REPORT_TYPE.ORGANIZATION) {
    const events = await db.event.findMany({
      where: { organizationIds: { has: entityId } },
      select: { id: true },
    });
    if (!events.length) {
      return null;
    }
    return {
      $match: {
        eventId: { $in: events.map((e) => ({ $oid: e.id })) },
        status: OrderStatus.COMPLETED,
      },
    };
  }

  return null;
};

/** Order → unwind OrderItems → OrderItem-shaped root with `order` already attached. */
const buildOrderRootToOrderItemStages = (
  orderItemMatchConditions: MongoPipelineStage[] = []
): MongoPipelineStage[] => {
  const lookupStage: MongoPipelineStage =
    orderItemMatchConditions.length > 0
      ? {
          $lookup: {
            from: "OrderItem",
            localField: "_id",
            foreignField: "orderId",
            pipeline: [{ $match: { $and: orderItemMatchConditions } }],
            as: "_orderItems",
          },
        }
      : {
          $lookup: {
            from: "OrderItem",
            localField: "_id",
            foreignField: "orderId",
            as: "_orderItems",
          },
        };

  return [
    lookupStage,
    { $unwind: { path: "$_orderItems" } },
    {
      $replaceRoot: {
        newRoot: {
          $let: {
            vars: {
              orderDoc: {
                $arrayToObject: {
                  $filter: {
                    input: { $objectToArray: "$$ROOT" },
                    as: "field",
                    cond: { $ne: ["$$field.k", "_orderItems"] },
                  },
                },
              },
            },
            in: {
              $mergeObjects: ["$_orderItems", { order: "$$orderDoc" }],
            },
          },
        },
      },
    },
  ];
};

export const getAttendeeReportScopeSize = async (
  entityId: string,
  type: REPORT_TYPE
): Promise<number> => {
  if (type === REPORT_TYPE.EVENT) {
    return reportingPrisma.orderItem.count({
      where: {
        order: {
          eventId: entityId,
          status: OrderStatus.COMPLETED,
        },
      },
    });
  }

  if (type === REPORT_TYPE.WHITE_LABEL) {
    return reportingPrisma.orderItem.count({
      where: {
        order: {
          status: OrderStatus.COMPLETED,
          event: {
            whiteLabelId: entityId,
          },
        },
      },
    });
  }

  if (type === REPORT_TYPE.ORGANIZATION) {
    return reportingPrisma.orderItem.count({
      where: {
        order: {
          status: OrderStatus.COMPLETED,
          event: {
            organizationIds: { has: entityId },
          },
        },
      },
    });
  }

  return 0;
};

const buildEmptyAttendeeReportAggregationResponse = (
  limit: number
): AttendeeReportAggregationRawResponse => ({
  result: [],
  stats: [{ total: 0, checkedIn: 0, checkedOut: 0, notCheckedIn: 0 }],
  pagination: [{ total: 0, limit, hasNextPage: false, hasPreviousPage: false }],
});

/** `$regexMatch` requires a string input; `$ifNull` alone still passes numbers/objects through. */
const regexMatchStringInput = (expr: unknown) => ({
  $convert: {
    input: expr,
    to: "string",
    onNull: "",
    onError: "",
  },
});
export const getAttendeeReport = async (
  entityId: string,
  sort: Record<string, "asc" | "desc">,
  pageNumber: number,
  pageSize: number,
  selectedDate?: Date,
  filters?: Record<string, any>,
  type?: REPORT_TYPE,
  lang?: string,
  zoneId?: string
) => {
  try {
    let orderCondition: any = {
      status: OrderStatus.COMPLETED,
    };

    if (type === REPORT_TYPE.WHITE_LABEL) {
      orderCondition = {
        ...orderCondition,
        event: { whiteLabelId: entityId },
      };
    } else {
      orderCondition = {
        ...orderCondition,
        eventId: entityId,
      };
    }

    // Scope via Order relation (eventId/status indexes) — do not materialize order IDs.
    const skip = (pageNumber - 1) * pageSize;
    const query: any = {
      where: {
        order: orderCondition,
        ...filters,
      },
    };
    // const include =
    const total = await prisma.orderItem.count(query);
    // Fetch the data and total count
    const [data, pagination, stats] = await Promise.all([
      prisma.orderItem.findMany({
        ...query,
        include: {
          formResults: {
            include: {
              formResult: {
                select: {
                  formTemplate: true,
                  id: true,
                  surveyResult: true,
                },
              },
            },
          },
          order: {
            include: {
              formResults: {
                include: {
                  formResult: {
                    select: {
                      surveyResult: true,
                    },
                  },
                },
              },
              profile: {
                select: {
                  name: true,
                },
              },
            },
          },
          ticket: {
            select: {
              id: true,
              //name: true,
              translations: lang
                ? {
                    where: {
                      language: {
                        code: lang,
                      },
                    },
                  }
                : true,
              price: true,
              category: {
                select: {
                  name: true,
                },
              },
              // translations: lang
              //   ? {
              //       where: {
              //         language: {
              //           code: lang,
              //         },
              //       },
              //       select: {
              //         name: true,
              //       },
              //     }
              //   : {
              //       select: {
              //         name: true,
              //       },
              //     },
            },
          },

          checksInOut: (() => {
            if (selectedDate?.setHours) {
              return {
                where: {
                  time: {
                    gte: new Date(selectedDate?.setHours(0, 0, 0, 0) || ""),
                    lte: new Date(
                      selectedDate?.setHours(23, 59, 59, 999) || ""
                    ),
                  },
                },
                include: {
                  createdBy: {
                    select: {
                      name: true,
                    },
                  },
                },
              };
            }

            if (zoneId) {
              return {
                where: {
                  zoneId: zoneId,
                },
                include: {
                  createdBy: {
                    select: {
                      name: true,
                    },
                  },
                },
              };
            }

            return {
              include: {
                createdBy: {
                  select: {
                    name: true,
                  },
                },
              },
            };
          })(),
          //  order: event: true,
        },

        orderBy: sort,
        skip,
        take: pageSize,
      }) as Promise<OrderItemWithReportRelations[]>,
      {
        total,
        hasNextPage: total > skip + pageSize,
      },
      {
        total,
        checkIn: await prisma.orderItem.count({
          where: {
            order: orderCondition,
            checksInOut: {
              some: {
                type: CheckInOutType.CHECK_IN,
              },
            },
          },
        }),
        checkOut: await prisma.orderItem.count({
          where: {
            order: orderCondition,

            checksInOut: {
              // last check in/out
              some: {
                type: CheckInOutType.CHECK_OUT,
              },
            },
          },
        }),
        notCheckIn: await prisma.orderItem.count({
          where: {
            order: orderCondition,

            checksInOut: {
              none: {
                type: CheckInOutType.CHECK_IN,
              },
            },
          },
        }),
      },
    ]);

    return {
      data: data.map((item) => {
        return {
          ...item,
          formResult: item.formResults?.[0],
          order: {
            ...item.order,
            formResult: item.order.formResults?.[0],
          },
        };
      }),
      pagination,
      stats,
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export type AttendeeReport = {
  data: (OrderItem & {
    formResult: {
      formTemplate: any;
      id: string;
      surveyResult: Record<string, any>;
    } | null;
    order: {
      id: string;
      number: number;
      total: number;
      status: string;
      currency: string;
      tax: number;
      expiryDate: Date;
      formResult: {
        formTemplate: any;
        id: string;
        surveyResult: Record<string, any>;
      } | null;
    };
    ticket: {
      id: string;
      translations: {
        name: string;
        description: string;
      }[];
      price: number;
      category: {
        name: string;
      };
    };
    checksInOut: {
      id: string;
      type: CheckInOutType;
      time: Date;
      createdBy: {
        name: string;
      };
    }[];
  })[];
  pagination: {
    total: number;
    hasNextPage: boolean;
  };
  stats: {
    total: number;
    checkIn: number;
    checkOut: number;
    notCheckIn: number;
    checkInByZone: {
      id: string;
      name: string;
      count: number;
    }[];
  };
};

export const getAttendeeStats = async (
  eventId: string,
  agentId?: string,
  lang?: string,
  categoryId?: string,
  ticketId?: string,
  zoneId?: string,
  entityTierId?: string
) => {
  try {
    let agentZones: any[] = [];
    if (agentId) {
      const zonesResult = await getZoneByAgentId(
        agentId,
        agentId,
        eventId,
        lang
      );
      agentZones = Array.isArray(zonesResult) ? zonesResult : [];
    }
    const orderCondition = {
      status: OrderStatus.COMPLETED,
      eventId,
    };

    // Build filter conditions for order items
    const filterConditions: any = {};
    if (categoryId) {
      filterConditions.ticket = { categoryId: categoryId };
    }
    if (ticketId) {
      filterConditions.ticketId = ticketId;
    }
    if (entityTierId) {
      filterConditions.entityTierId = entityTierId;
    }

    // Always scope CheckInOut groupBy to this event's confirmed items.
    // Unscoped groupBy previously scanned the whole CheckInOut collection.
    const eventScopedOrderItem = {
      status: OrderItemStatus.CONFIRMED,
      order: orderCondition,
      ...filterConditions,
    };

    const checkInByZone = await prisma.checkInOut.groupBy({
      by: ["zoneId"],
      _count: true,
      where: {
        type: CheckInOutType.CHECK_IN,
        orderItem: eventScopedOrderItem,
        ...(zoneId ? { zoneId } : {}),
      },
    });
    const checkOutByZone = await prisma.checkInOut.groupBy({
      by: ["zoneId"],
      _count: true,
      where: {
        type: CheckInOutType.CHECK_OUT,
        orderItem: eventScopedOrderItem,
        ...(zoneId ? { zoneId } : {}),
      },
    });
    const zones = await prisma.zone.findMany({
      where: {
        ...(agentId && {
          doorAgents: {
            some: {
              id: agentId,
            },
          },
        }),
        venue: {
          eventIds: { has: eventId },
        },
      },
      include: {
        translations: {
          where: {
            language: { code: lang ?? "en" },
          },
        },
      },
    });
    const checkInByZoneWithNames = zones
      .filter((zone) =>
        agentZones.length > 0 ? !agentZones.includes(zone.id) : zone
      )
      .map((zone: any) => {
        const zoneCount = checkInByZone.find((item) => zone.id === item.zoneId);
        return {
          name: zone.translations?.[0]?.name,
          id: zone.id,
          count: zoneCount?._count || 0,
        };
      });
    const checkOutByZoneWithNames = zones
      .filter((zone) =>
        agentZones.length > 0 ? !agentZones.includes(zone.id) : zone
      )
      .map((zone: any) => {
        const zoneCount = checkOutByZone.find(
          (item) => zone.id === item.zoneId
        );
        return {
          name: zone.translations?.[0]?.name,
          id: zone.id,
          count: zoneCount?._count || 0,
        };
      });

    const forAgent = agentZones.length > 0;
    const orderItemMatch: Prisma.InputJsonObject = {
      $expr: { $eq: ["$orderId", "$$orderId"] },
      status: OrderItemStatus.CONFIRMED,
      ...(ticketId && isMongoObjectId(ticketId)
        ? { ticketId: { $oid: ticketId } }
        : {}),
      ...(entityTierId && isMongoObjectId(entityTierId)
        ? { entityTierId: { $oid: entityTierId } }
        : {}),
    };

    const orderItemPipeline: MongoPipelineStage[] = [
      { $match: orderItemMatch },
    ];
    if (categoryId && isMongoObjectId(categoryId)) {
      orderItemPipeline.push(
        {
          $lookup: {
            from: "Ticket",
            localField: "ticketId",
            foreignField: "_id",
            pipeline: [{ $project: { categoryId: 1 } }],
            as: "_ticket",
          },
        },
        {
          $match: {
            "_ticket.0.categoryId": { $oid: categoryId },
          },
        }
      );
    }
    orderItemPipeline.push(
      buildLatestCheckInOutLookup({
        as: "latestCheck",
        zoneOid:
          zoneId && isMongoObjectId(zoneId) ? { $oid: zoneId } : undefined,
      }) as MongoPipelineStage,
      {
        $project: {
          latestType: { $arrayElemAt: ["$latestCheck.type", 0] },
        },
      }
    );

    const latestStatusCounts = (await prisma.order.aggregateRaw({
      pipeline: [
        {
          $match: {
            eventId: { $oid: eventId },
            status: OrderStatus.COMPLETED,
          },
        },
        { $project: { _id: 1 } },
        {
          $lookup: {
            from: "OrderItem",
            let: { orderId: "$_id" },
            pipeline: orderItemPipeline,
            as: "items",
          },
        },
        { $unwind: { path: "$items" } },
        {
          $group: {
            _id: null,
            total: { $sum: 1 },
            checkIn: {
              $sum: {
                $cond: [
                  { $eq: ["$items.latestType", CheckInOutType.CHECK_IN] },
                  1,
                  0,
                ],
              },
            },
            checkOut: {
              $sum: {
                $cond: [
                  { $eq: ["$items.latestType", CheckInOutType.CHECK_OUT] },
                  1,
                  0,
                ],
              },
            },
          },
        },
      ] satisfies MongoPipelineStage[],
    })) as unknown as Array<{
      total?: number;
      checkIn?: number;
      checkOut?: number;
    }>;

    const total = latestStatusCounts[0]?.total ?? 0;
    const checkInCount = latestStatusCounts[0]?.checkIn ?? 0;
    const checkOutCount = latestStatusCounts[0]?.checkOut ?? 0;
    const notCheckInCount = total - checkInCount - checkOutCount;

    const stats = {
      total,
      checkIn: forAgent
        ? checkInByZoneWithNames.reduce((acc, curr) => acc + curr.count, 0)
        : checkInCount,
      checkOut: forAgent
        ? checkOutByZoneWithNames.reduce((acc, curr) => acc + curr.count, 0)
        : checkOutCount,
      notCheckIn: forAgent
        ? total -
          checkInByZoneWithNames.reduce((acc, curr) => acc + curr.count, 0)
        : notCheckInCount,
      checkInByZone: checkInByZoneWithNames,
      checkOutByZone: checkOutByZoneWithNames,
    };
    return stats;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

interface SearchOptions {
  searchFields?: string[];
  caseSensitive?: boolean;
  exactMatch?: boolean;
  limit?: number;
}

function normalizeAttendeeSearchField(field: string): string {
  if (field === "dtcmBarCode") {
    return "dtcmBarcode";
  }

  return field;
}

export const getSearchedAttendeesByEventId = async (
  eventId: string,
  query: string,
  zoneId?: string,
  lang?: string,
  selectedDate?: Date,
  options: SearchOptions = {}
) => {
  const {
    searchFields: rawSearchFields = [
      "ticket",
      "order",
      "name",
      "email",
      "phone",
      "company",
      "orderItem",
      "dtcmBarcode",
    ],
    caseSensitive = false,
    exactMatch = false,
    limit,
  } = options;

  const searchFields = rawSearchFields.map(normalizeAttendeeSearchField);
  const normalizedQuery = normalizeScanQuery(query);

  const emptyResults = (): Record<string, any[]> => {
    const r: Record<string, any[]> = {};
    searchFields.forEach((f) => {
      r[f] = [];
    });
    return r;
  };

  // ─── Pre-fetches ────────────────────────────────────────────────────────────
  // 1. Completed order IDs for this event → enables early $match
  const orderDocs = await prisma.order.findMany({
    where: { eventId, status: OrderStatus.COMPLETED },
    select: { id: true },
  });
  if (orderDocs.length === 0) return emptyResults();
  const orderIdOids = orderDocs.map((o) => ({ $oid: o.id }));

  // 2. Zone-accessible ticket IDs (if zone filter is active)
  let zoneTicketOids: { $oid: string }[] | null = null;
  if (zoneId) {
    const zoneTickets = await prisma.ticket.findMany({
      where: { bookableItem: { zones: { some: { id: zoneId } } } },
      select: { id: true },
    });
    if (zoneTickets.length === 0) return emptyResults();
    zoneTicketOids = zoneTickets.map((t) => ({ $oid: t.id }));
  }

  // ─── Build search conditions ─────────────────────────────────────────────────
  const escapedQuery = normalizedQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regexOptions = caseSensitive ? "" : "i";
  const regexPattern = exactMatch ? `^${escapedQuery}$` : escapedQuery;
  const queryAsNumber = !isNaN(Number(normalizedQuery))
    ? Number(normalizedQuery)
    : null;
  const isObjectId = isMongoObjectId(normalizedQuery);

  // surveyResult field shapes vary by version; coerce to string before $regexMatch.
  const searchOrConditions: any[] = [];
  for (const field of searchFields) {
    switch (field) {
      case "ticket":
        searchOrConditions.push({
          $regexMatch: {
            input: regexMatchStringInput({ $ifNull: ["$_categoryName", ""] }),
            regex: regexPattern,
            options: regexOptions,
          },
        });
        break;
      case "order":
        if (queryAsNumber !== null)
          searchOrConditions.push({ $eq: ["$_orderNumber", queryAsNumber] });
        if (isObjectId)
          searchOrConditions.push({
            $eq: ["$orderId", { $oid: normalizedQuery }],
          });
        break;
      case "orderItem":
        if (isObjectId)
          searchOrConditions.push({ $eq: ["$_id", { $oid: normalizedQuery }] });
        break;
      case "dtcmBarcode":
        searchOrConditions.push({ $eq: ["$dtcmBarcode", normalizedQuery] });
        break;
      case "name":
        searchOrConditions.push(
          {
            $regexMatch: {
              input: regexMatchStringInput({
                $ifNull: ["$_ticketSurvey.name", ""],
              }),
              regex: regexPattern,
              options: regexOptions,
            },
          },
          {
            $regexMatch: {
              input: regexMatchStringInput({
                $ifNull: ["$_orderSurvey.name", ""],
              }),
              regex: regexPattern,
              options: regexOptions,
            },
          }
        );
        break;
      case "email":
        searchOrConditions.push(
          {
            $regexMatch: {
              input: regexMatchStringInput({
                $ifNull: ["$_ticketSurvey.email", ""],
              }),
              regex: regexPattern,
              options: regexOptions,
            },
          },
          {
            $regexMatch: {
              input: regexMatchStringInput({
                $ifNull: ["$_orderSurvey.email", ""],
              }),
              regex: regexPattern,
              options: regexOptions,
            },
          }
        );
        break;
      case "phone":
        searchOrConditions.push({
          $regexMatch: {
            input: regexMatchStringInput({
              $ifNull: ["$_ticketSurvey.phone", ""],
            }),
            regex: regexPattern,
            options: regexOptions,
          },
        });
        break;
      case "company":
        searchOrConditions.push({
          $regexMatch: {
            input: regexMatchStringInput({
              $ifNull: ["$_ticketSurvey.company", ""],
            }),
            regex: regexPattern,
            options: regexOptions,
          },
        });
        break;
    }
  }
  if (searchOrConditions.length === 0) return emptyResults();

  // ─── Phase 1: lightweight aggregation → find matching IDs ───────────────────
  // Only minimal joins (no formTemplates, no checksInOut, no transitions, no event).
  // Returns at most `limit` IDs — the expensive full-data fetch in Phase 2 only
  // runs for this tiny set.
  const searchPipeline: any[] = [
    // Early filter: items belonging to this event's completed orders
    {
      $match: {
        orderId: { $in: orderIdOids },
        status: OrderItemStatus.CONFIRMED,
        ...(zoneTicketOids ? { ticketId: { $in: zoneTicketOids } } : {}),
      },
    },

    // Order number (needed for "order" search field)
    {
      $lookup: {
        from: "Order",
        let: { oid: "$orderId" },
        pipeline: [
          { $match: { $expr: { $eq: ["$_id", "$$oid"] } } },
          { $project: { number: 1 } },
        ],
        as: "_orderDoc",
      },
    },
    { $unwind: { path: "$_orderDoc", preserveNullAndEmptyArrays: true } },

    // Ticket → category name (needed for "ticket" search field)
    {
      $lookup: {
        from: "Ticket",
        let: { tid: "$ticketId" },
        pipeline: [
          { $match: { $expr: { $eq: ["$_id", "$$tid"] } } },
          { $project: { categoryId: 1 } },
        ],
        as: "_ticketDoc",
      },
    },
    { $unwind: { path: "$_ticketDoc", preserveNullAndEmptyArrays: true } },
    {
      $lookup: {
        from: "TicketCategory",
        let: { cid: "$_ticketDoc.categoryId" },
        pipeline: [
          { $match: { $expr: { $eq: ["$_id", "$$cid"] } } },
          { $project: { name: 1 } },
        ],
        as: "_categoryDoc",
      },
    },
    { $unwind: { path: "$_categoryDoc", preserveNullAndEmptyArrays: true } },

    // Per-attendee form result surveyResult (name/email/phone/company)
    {
      $lookup: {
        from: "FormResultEntity",
        let: { oiid: "$_id" },
        pipeline: [
          { $match: { $expr: { $eq: ["$orderItemId", "$$oiid"] } } },
          { $limit: 1 },
        ],
        as: "_fre",
      },
    },
    { $unwind: { path: "$_fre", preserveNullAndEmptyArrays: true } },
    {
      $lookup: {
        from: "FormResult",
        let: { frid: "$_fre.formResultId" },
        pipeline: [
          { $match: { $expr: { $eq: ["$_id", "$$frid"] } } },
          { $project: { surveyResult: 1 } },
        ],
        as: "_ticketFormResult",
      },
    },
    {
      $unwind: { path: "$_ticketFormResult", preserveNullAndEmptyArrays: true },
    },

    // Order-level form result surveyResult (buyer name/email)
    {
      $lookup: {
        from: "FormResultEntity",
        let: { oid: "$orderId" },
        pipeline: [
          {
            $match: {
              $expr: {
                $and: [
                  { $eq: ["$orderId", "$$oid"] },
                  { $eq: ["$type", "ORDER"] },
                ],
              },
            },
          },
          { $limit: 1 },
        ],
        as: "_ofre",
      },
    },
    { $unwind: { path: "$_ofre", preserveNullAndEmptyArrays: true } },
    {
      $lookup: {
        from: "FormResult",
        let: { frid: "$_ofre.formResultId" },
        pipeline: [
          { $match: { $expr: { $eq: ["$_id", "$$frid"] } } },
          { $project: { surveyResult: 1 } },
        ],
        as: "_orderFormResult",
      },
    },
    {
      $unwind: { path: "$_orderFormResult", preserveNullAndEmptyArrays: true },
    },

    // Promote search-relevant fields to the top level
    {
      $addFields: {
        _orderNumber: "$_orderDoc.number",
        _categoryName: "$_categoryDoc.name",
        _ticketSurvey: "$_ticketFormResult.surveyResult",
        _orderSurvey: "$_orderFormResult.surveyResult",
      },
    },

    // Apply the search filter at the DB level
    { $match: { $expr: { $or: searchOrConditions } } },

    // Return only the _id (no offset skip — callers use limit only; skip defaults to 0)
    ...(limit ? [{ $limit: limit }] : []),
    { $project: { _id: 1 } },
  ];

  const matchedRaw = (await prisma.orderItem.aggregateRaw({
    pipeline: searchPipeline,
  })) as unknown as any[];

  if (!matchedRaw || matchedRaw.length === 0) return emptyResults();

  const matchedIds = matchedRaw.map((doc: any) =>
    typeof doc._id === "string" ? doc._id : doc._id?.$oid ?? String(doc._id)
  );

  // ─── Phase 2: fetch full data for the small matched set only ────────────────
  const orderItemsArray = await prisma.orderItem.findMany({
    where: { id: { in: matchedIds } },
    include: {
      formResults: {
        include: {
          formResult: {
            select: {
              formTemplate: true,
              id: true,
              surveyResult: true,
            },
          },
        },
      },
      order: {
        select: {
          id: true,
          number: true,
          total: true,
          status: true,
          currency: true,
          tax: true,
          expiryDate: true,
          createdAt: true,
          formResults: {
            include: {
              formResult: {
                select: {
                  id: true,
                  surveyResult: true,
                  formTemplate: true,
                },
              },
            },
          },
          profile: {
            select: {
              id: true,
              name: true,
              email: true,
              phoneNumber: true,
              user: { select: { image: true } },
            },
          },
          transitions: {
            orderBy: { createdAt: "desc" },
            take: 1,
            select: { id: true, createdBy: true },
          },
          event: {
            select: {
              id: true,
              startDate: true,
              translations: lang
                ? {
                    where: { language: { code: lang } },
                    select: { name: true, description: true },
                  }
                : { select: { name: true, description: true } },
            },
          },
        },
      },
      ticket: {
        select: {
          id: true,
          bookableItem: {
            select: {
              translations: lang
                ? { where: { language: { code: lang } } }
                : true,
              price: true,
            },
          },
          category: { select: { name: true } },
        },
      },
      checksInOut: selectedDate?.setHours
        ? {
            where: {
              time: {
                gte: new Date(selectedDate?.setHours(0, 0, 0, 0) || ""),
                lte: new Date(selectedDate?.setHours(23, 59, 59, 999) || ""),
              },
            },
            orderBy: { time: "asc" },
            include: { createdBy: { select: { name: true } } },
          }
        : {
            where: { zoneId },
            orderBy: { time: "asc" },
            include: { createdBy: { select: { name: true } } },
          },
    },
  });

  // ─── Transform ───────────────────────────────────────────────────────────────
  const orderItems = orderItemsArray.map((item) => ({
    ...item,
    formResult: {
      ...item?.formResults?.[0]?.formResult,
      surveyResult: item?.formResults?.[0]?.formResult?.surveyResult
        ? transformSurveyResult(
            item?.formResults?.[0]?.formResult?.formTemplate?.json,
            item?.formResults?.[0]?.formResult?.surveyResult
          )
        : null,
    },
    ticket: {
      ...item?.ticket,
      ...item?.ticket?.bookableItem?.translations[0],
      id: item?.ticket?.id,
    },
    order: {
      ...item?.order,
      formResult: {
        ...item?.order?.formResults?.[0],
        surveyResult: item?.order?.formResults?.[0]?.formResult?.surveyResult
          ? transformSurveyResult(
              item?.order?.formResults?.[0]?.formResult?.formTemplate?.json,
              item?.order?.formResults?.[0]?.formResult?.surveyResult
            )
          : null,
      },
    },
  }));

  // ─── Phase 3: assign to per-field buckets (trivially fast — small set) ───────
  // Preserve the { [fieldName]: items[] } response shape for `defaultRes=true` callers.
  const searchInField = (value: string | undefined | null): boolean => {
    if (!value) return false;
    if (exactMatch) {
      return caseSensitive
        ? value === normalizedQuery
        : value.toLowerCase() === normalizedQuery.toLowerCase();
    }
    return caseSensitive
      ? value.includes(normalizedQuery)
      : value.toLowerCase().includes(normalizedQuery.toLowerCase());
  };

  const results: Record<string, any[]> = {};
  searchFields.forEach((field) => {
    switch (field) {
      case "ticket":
        results[field] = orderItems.filter((item) =>
          searchInField(item.ticket?.category?.name)
        );
        break;
      case "order":
        results[field] = orderItems.filter((item) =>
          searchInField(item.order?.number?.toString())
        );
        break;
      case "orderItem":
        results[field] = orderItems.filter(
          (item) =>
            searchInField(item.id) || searchInField(item.order?.profile?.id)
        );
        break;
      case "name":
        results[field] = orderItems.filter(
          (item) =>
            searchInField(item.formResult?.surveyResult?.name?.value) ||
            searchInField(item.order?.formResult?.surveyResult?.name?.value)
        );
        break;
      case "email":
        results[field] = orderItems.filter(
          (item) =>
            searchInField(item.formResult?.surveyResult?.email?.value) ||
            searchInField(item.order?.formResult?.surveyResult?.email?.value)
        );
        break;
      case "phone":
        results[field] = orderItems.filter((item) =>
          searchInField(item.formResult?.surveyResult?.phone?.value)
        );
        break;
      case "company":
        results[field] = orderItems.filter((item) =>
          searchInField(item.formResult?.surveyResult?.company?.value)
        );
        break;
      case "dtcmBarcode":
        results[field] = orderItems.filter(
          (item) => item.dtcmBarcode === normalizedQuery
        );
        break;
      default:
        results[field] = [];
    }
  });

  return results;
};

export const attendeeReportAggregation = async (
  id: any,
  limit: number,
  searchParams: { [key: string]: string },
  type: REPORT_TYPE,
  paginate: boolean = true,
  lang?: string,
  deselectedColumns?: any,
  exported?: boolean,
  isDTCMEvent?: boolean,
  ids?: string[],
  userId?: string,
  cursorId?: string,
  beforeCursor?: string
): Promise<AttendeeReportAggregationRawResponse> => {
  try {
    const prisma = exported ? exportPrisma : reportingPrisma;
    const scopeEntityId = typeof id === "string" ? id : undefined;

    // Resolve timezone from the event when available. Fallback to UTC.
    let eventTimeZone = "UTC";
    if (type === REPORT_TYPE.EVENT && id) {
      const event = await prisma.event.findUnique({
        where: { id },
        select: { timezone: true },
      });
      eventTimeZone = event?.timezone || "UTC";
    }
    const languageRecord = lang
      ? await prisma.language.findUnique({
          where: { code: lang },
          select: { id: true },
        })
      : null;
    const languageOid = languageRecord ? { $oid: languageRecord.id } : null;
    let zones: any[] = [];
    if (userId) {
      const zoneResult = await getZoneByAgentId(userId, userId, id, lang);
      zones = Array.isArray(zoneResult) ? zoneResult : [];
    }
    // When the request is scoped to a door agent, restrict results to the tickets
    // accessible by the agent's zones.
    const accessibleTicketOids = userId
      ? Array.from(
          new Set(
            (zones || [])
              .flatMap((z) => z?.accessibleTickets || [])
              .map((t) => t?.id)
              .filter(Boolean)
          )
        ).map((ticketId) => ({ $oid: ticketId as string }))
      : [];
    const {
      attendee,
      zone,
      zoneId,
      search,
      group,
      ticketId,
      categoryId,
      entityTierId,
      agentId,
      badgeStatus,
    } = searchParams || {};

    let filtrationStages: any[] = [];

    const isValidObjectId = (id: string) => {
      return /^[a-f\d]{24}$/i.test(id);
    };
    const searchAsNumber = !isNaN(Number(search)) ? Number(search) : null;
    // Escape regex special characters to prevent MongoDB $regexMatch errors
    const searchAsString = search
      ? search.toString().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      : null;

    const canPreselectPageBeforeEnrich =
      paginate &&
      limit > 0 &&
      !exported &&
      scopeEntityId &&
      (!ids || ids.length === 0) &&
      !search &&
      !group &&
      !agentId &&
      !userId &&
      (!attendee || attendee === "all") &&
      !zone &&
      !zoneId &&
      !categoryId;

    if (canPreselectPageBeforeEnrich && scopeEntityId) {
      const orderScopeMatch = await resolveCompletedOrderScopeMatch(
        scopeEntityId,
        type,
        prisma
      );

      if (!orderScopeMatch) {
        return buildEmptyAttendeeReportAggregationResponse(limit);
      }

      const orderItemMatchConditions: MongoPipelineStage[] = [];
      if (ticketId && isValidObjectId(ticketId)) {
        orderItemMatchConditions.push({ ticketId: { $oid: ticketId } });
      }
      if (badgeStatus === "printed") {
        orderItemMatchConditions.push({ printed: { $eq: true } });
      } else if (badgeStatus === "not_printed") {
        orderItemMatchConditions.push({ printed: { $ne: true } });
      }
      if (
        entityTierId &&
        entityTierId !== "" &&
        isValidObjectId(entityTierId)
      ) {
        orderItemMatchConditions.push({ entityTierId: { $oid: entityTierId } });
      }

      const reversePreselect = beforeCursor && isValidObjectId(beforeCursor);
      const forwardPreselect =
        !reversePreselect && cursorId && isValidObjectId(cursorId);
      const pageLimit = limit + 1;
      const pageStages: MongoPipelineStage[] = reversePreselect
        ? [
            { $match: { _id: { $gt: { $oid: beforeCursor as string } } } },
            { $sort: { _id: 1 } },
            { $limit: pageLimit },
            { $sort: { _id: -1 } },
            { $project: { _id: 1 } },
          ]
        : forwardPreselect
        ? [
            { $match: { _id: { $lt: { $oid: cursorId as string } } } },
            { $sort: { _id: -1 } },
            { $limit: pageLimit },
            { $project: { _id: 1 } },
          ]
        : [
            { $sort: { _id: -1 } },
            { $limit: pageLimit },
            { $project: { _id: 1 } },
          ];

      // Start from Order (indexed eventId + status), then unwind OrderItems.
      const preselected = (await prisma.order.aggregateRaw({
        pipeline: [
          orderScopeMatch,
          ...buildOrderRootToOrderItemStages(orderItemMatchConditions),
          {
            $facet: {
              page: pageStages,
              stats: [
                buildLatestCheckInOutLookup() as MongoPipelineStage,
                {
                  $addFields: {
                    Status: latestCheckInOutStatusExpression(),
                  },
                },
                {
                  $group: {
                    _id: null,
                    total: { $sum: 1 },
                    checkedIn: {
                      $sum: {
                        $cond: [
                          { $eq: ["$Status", CheckInOutType.CHECK_IN] },
                          1,
                          0,
                        ],
                      },
                    },
                    checkedOut: {
                      $sum: {
                        $cond: [
                          { $eq: ["$Status", CheckInOutType.CHECK_OUT] },
                          1,
                          0,
                        ],
                      },
                    },
                    notCheckedIn: {
                      $sum: {
                        $cond: [
                          { $eq: ["$Status", "NOT_CHECK_IN"] },
                          1,
                          0,
                        ],
                      },
                    },
                  },
                },
              ],
              pagination: [
                {
                  $group: {
                    _id: null,
                    total: { $sum: 1 },
                  },
                },
              ],
            },
          },
        ],
      })) as unknown as Array<{
        page?: Array<{ _id?: { $oid?: string } | string }>;
        stats?: AttendeeReportAggregationRawResponse["stats"];
        pagination?: Array<{ total?: number }>;
      }>;

      const pageRows = preselected[0]?.page ?? [];
      const fetchedExtra = pageRows.length > limit;
      const selectedPageRows =
        reversePreselect && fetchedExtra
          ? pageRows.slice(1)
          : pageRows.slice(0, limit);
      const pageIds = selectedPageRows
        .map((row) => (typeof row._id === "string" ? row._id : row._id?.$oid))
        .filter((rowId): rowId is string => Boolean(rowId));

      const enrichedPage = pageIds.length
        ? await attendeeReportAggregation(
            id,
            -1,
            searchParams,
            type,
            false,
            lang,
            deselectedColumns,
            false,
            isDTCMEvent,
            pageIds,
            userId
          )
        : buildEmptyAttendeeReportAggregationResponse(limit);

      return {
        ...enrichedPage,
        stats: preselected[0]?.stats ?? [],
        pagination: [
          {
            total: preselected[0]?.pagination?.[0]?.total ?? 0,
            limit,
            hasPreviousPage: reversePreselect
              ? fetchedExtra
              : Boolean(forwardPreselect),
            hasNextPage: reversePreselect ? true : fetchedExtra,
          },
        ],
      };
    }

    const matchConditions: any[] = [];

    if (ids && ids.length > 0) {
      const validIds = ids.filter((id) => id && isValidObjectId(id));
      if (validIds.length > 0) {
        if (group === REPORT_GROUP_BY.EVENT_DATE) {
          matchConditions.push({
            "event._id": { $in: validIds.map((id) => ({ $oid: id })) },
          });
        } else if (group === REPORT_GROUP_BY.TICKET_TYPE) {
          matchConditions.push({
            $or: [
              { "category._id": { $in: validIds.map((id) => ({ $oid: id })) } },
              { "ticket._id": { $in: validIds.map((id) => ({ $oid: id })) } },
            ],
          });
        } else {
          matchConditions.push({
            _id: { $in: validIds.map((id) => ({ $oid: id })) },
          });
        }
      }
    }

    // ticketId / badge / attendee / zones / category / entityTier are applied earlier
    // (root $match before Order lookup, or immediately after CheckInOut lookup) —
    // not duplicated here alongside search/agentId-specific late filters.

    if (agentId && isValidObjectId(agentId)) {
      matchConditions.push({
        "uniqueAgents.id": { $oid: agentId },
      });
    }

    if (search) {
      matchConditions.push({
        $or: [
          // Match order ID (ObjectId)
          isValidObjectId(search) ? { "order._id": { $oid: search } } : null,
          // Match order item ID (ObjectId)
          isValidObjectId(search) ? { _id: { $oid: search } } : null,
          // Match ticket ID (ObjectId)
          isValidObjectId(search) ? { "ticket._id": { $oid: search } } : null,
          // Match order number (numeric)
          searchAsNumber !== null ? { "order.number": searchAsNumber } : null,
          // Match buyer name (computed field)
          {
            $expr: {
              $regexMatch: {
                regex: searchAsString,
                input: regexMatchStringInput({ $ifNull: ["$buyerName", ""] }),
                options: "i",
              },
            },
          },
          // Match buyer email (computed field)
          {
            $expr: {
              $regexMatch: {
                regex: searchAsString,
                input: regexMatchStringInput({ $ifNull: ["$buyerEmail", ""] }),
                options: "i",
              },
            },
          },
          // Match phone / mobile number (computed field)
          {
            $expr: {
              $regexMatch: {
                regex: searchAsString,
                input: regexMatchStringInput({ $ifNull: ["$Phone", ""] }),
                options: "i",
              },
            },
          },
          // Match ticket type name (from ticket translations)
          {
            $expr: {
              $regexMatch: {
                regex: searchAsString,
                input: regexMatchStringInput({
                  $ifNull: [
                    { $arrayElemAt: ["$ticket.translations.name", 0] },
                    "",
                  ],
                }),
                options: "i",
              },
            },
          },
          // Match ticket group / category name
          {
            $expr: {
              $regexMatch: {
                regex: searchAsString,
                input: regexMatchStringInput({
                  $ifNull: ["$category.name", ""],
                }),
                options: "i",
              },
            },
          },
          // Match company from ticket form result only (per-attendee field)
          {
            $expr: {
              $regexMatch: {
                regex: searchAsString,
                input: regexMatchStringInput({
                  $ifNull: [
                    {
                      $let: {
                        vars: {
                          companyField: {
                            $arrayElemAt: [
                              {
                                $filter: {
                                  input: {
                                    $objectToArray: {
                                      $ifNull: [
                                        "$formResultEntity.formResult.surveyResult",
                                        {},
                                      ],
                                    },
                                  },
                                  as: "result",
                                  cond: {
                                    $eq: ["$$result.k", "company"],
                                  },
                                },
                              },
                              0,
                            ],
                          },
                        },
                        in: { $ifNull: ["$$companyField.v", ""] },
                      },
                    },
                    "",
                  ],
                }),
                options: "i",
              },
            },
          },
        ].filter(Boolean),
      });
    }

    if (matchConditions.length > 0) {
      filtrationStages = [
        {
          $match: {
            $and: matchConditions,
          },
        },
      ];
    }

    let groupByEventStage: any[] = [];
    let projectStage: any[] = [];

    if (group && group == REPORT_GROUP_BY.EVENT_DATE) {
      groupByEventStage = [
        {
          $group: {
            _id: "$event._id",
            "Event Name": { $first: "$event.eventTranslations.name" },
            "Event Date": { $first: "$event.startDate" },
            Attendees: { $sum: 1 },
            Remaining: {
              $sum: {
                $cond: [{ $eq: ["$Status", CheckInOutType.CHECK_IN] }, 1, 0],
              },
            },
          },
        },
      ];
      projectStage = [
        {
          $project: {
            "Event Name": { $arrayElemAt: ["$Event Name", 0] },
            "Event Date": {
              $dateToString: {
                format: "%Y-%m-%d %H:%M:%S",
                date: "$Event Date",
                timezone: eventTimeZone,
              },
            },
            Attendees: 1,
            Remaining: 1,
            _id: 0,
            id: { $toString: "$_id" },
          },
        },
      ];
    } else if (group && group == REPORT_GROUP_BY.TICKET_TYPE) {
      groupByEventStage = [
        {
          $group: {
            _id: {
              $ifNull: ["$category._id", "$ticket._id"],
            },
            "Ticket Type": {
              $first: {
                $ifNull: [
                  "$category.name",
                  { $arrayElemAt: ["$ticket.translations.name", 0] },
                ],
              },
            },
            "Event Date and time": { $first: "$event.startDate" },
            Attendees: { $sum: 1 },
            Qty: { $sum: 1 },
            Price: { $sum: "$order.total" },
            Remaining: {
              $sum: {
                $cond: [{ $eq: ["$Status", CheckInOutType.CHECK_IN] }, 1, 0],
              },
            },
          },
        },
      ];
      projectStage = [
        {
          $project: {
            "Ticket Type": "$Ticket Type",
            Qty: 1,
            Price: 1,
            "Event Date and time": {
              $dateToString: {
                format: "%Y-%m-%d %H:%M:%S",
                date: "$Event Date and time",
                timezone: eventTimeZone,
              },
            },
            Attendees: 1,
            Remaining: 1,
            _id: 0,
            id: { $toString: "$_id" },
          },
        },
      ];
    }

    const baseProjectStage = [
      {
        $project: {
          contact: 1,
          ticket: 1,
          invitation: 1,
          isTicketAccessibleByZones: 1,
          checksInOut: 1,
          orderItemId: "$_id",
          orderFormResult: {
            formResult: "$orderFormResultEntity.formResult",
            id: { $toString: "$orderFormResultEntity.formResult._id" },
          },
          formResultEntity: {
            formResult: "$formResultEntity.formResult",
            id: { $toString: "$formResultEntity.formResult._id" },
          },
          _id: 0,
          "Buyer Name": {
            $ifNull: [
              "$orderFormResultEntity.formResult.surveyResult.name",
              { $ifNull: ["$buyerName", "$contact.name"] },
              "",
            ],
          },
          Status: 1,
          "Badge Printing Status": "$printed",
          "Badge Printing Status Date and Time": {
            $dateToString: {
              format: "%Y-%m-%d %H:%M:%S",
              date: "$order.printedAt",
              timezone: eventTimeZone,
            },
          },
          "Access Type": "$order.type",
          "Ticket/Registration Name": {
            $ifNull: [
              { $arrayElemAt: ["$ticket.translations.name", 0] },
              { $arrayElemAt: ["$invitation.translations.name", 0] },
            ],
          },
          "Ticket Holder Name": {
            $ifNull: ["$formResultEntity.formResult.surveyResult.name", ""],
          },
          "Ticket Holder Email": {
            $ifNull: ["$formResultEntity.formResult.surveyResult.email", ""],
          },
          "Ticket Holder Phone": {
            $ifNull: ["$formResultEntity.formResult.surveyResult.phone", ""],
          },
          "Ticket Holder Company": {
            $ifNull: ["$formResultEntity.formResult.surveyResult.company", ""],
          },
          "Buyer Company": {
            $ifNull: [
              "$orderFormResultEntity.formResult.surveyResult.company",
              "",
            ],
          },
          "Buyer Email": {
            $ifNull: [
              "$orderFormResultEntity.formResult.surveyResult.email",
              {
                $ifNull: [
                  "$formResultEntity.formResult.surveyResult.email",
                  {
                    $ifNull: [
                      "$buyerEmail",
                      { $ifNull: ["$contact.email", ""] },
                    ],
                  },
                ],
              },
            ],
          },
          "Buyer Phone": {
            $ifNull: [
              "$orderFormResultEntity.formResult.surveyResult.phone",
              {
                $ifNull: [
                  "$formResultEntity.formResult.surveyResult.phone",
                  { $ifNull: ["$contact.phoneNumber", ""] },
                ],
              },
            ],
          },
          "Ticket Group": {
            $ifNull: ["$category.name", ""],
          },
          "Event ID": { $toString: "$event._id" },
          "Event Name": {
            $arrayElemAt: ["$event.eventTranslations.name", 0],
          },
          "Event Date and time": {
            $dateToString: {
              format: "%Y-%m-%d %H:%M:%S",
              date: "$event.startDate",
              timezone: eventTimeZone,
            },
          },
          "Order Creation Date and time": {
            $dateToString: {
              format: "%Y-%m-%d %H:%M:%S",
              date: "$order.createdAt",
              timezone: eventTimeZone,
            },
          },
          "Last Checked in Date and time": {
            $dateToString: {
              format: "%Y-%m-%d %H:%M:%S",
              date: "$lastCheckIn.time",
              timezone: eventTimeZone,
            },
          },
          "Checked In From": {
            $cond: [
              { $ifNull: ["$lastCheckOut.from", false] },
              "$lastCheckOut.from",
              "-",
            ],
          },
          "Time Spent": 1,
          "Last Checked Out Date and time": {
            $dateToString: {
              format: "%Y-%m-%d %H:%M:%S",
              date: "$lastCheckOut.time",
              timezone: eventTimeZone,
            },
          },
          "Ticket/Registration Id": {
            $ifNull: [
              { $toString: "$ticket._id" },
              { $toString: "$invitation._id" },
            ],
          },
          "Order ID": { $toString: "$order.number" },
          OrderID: { $toString: "$order._id" },
          "Responsible Agent Name": {
            $reduce: {
              input: {
                $map: {
                  input: "$uniqueAgents",
                  as: "agent",
                  in: "$$agent.name",
                },
              },
              initialValue: "",
              in: {
                $concat: [
                  "$$value",
                  { $cond: [{ $eq: ["$$value", ""] }, "", ", "] },
                  "$$this",
                ],
              },
            },
          },
          Phone: 1,
          "Badge Printing Date and Time": {
            $dateToString: {
              format: "%Y-%m-%d %H:%M:%S",
              date: "$printedAt",
              timezone: eventTimeZone,
            },
          },
          orderDynamicField: 1,
          ticketDynamicField: 1,
          dynamicField: {
            $mergeObjects: ["$orderDynamicField", "$ticketDynamicField"],
          },
          ...(isDTCMEvent
            ? {
                DTCMPriceCategory: "$ticket.dtcmTicketPrice.category.name",
                DTCMTicketPrice: "$ticket.dtcmTicketPrice.typeCode",
                dtcmBarcode: { $ifNull: ["$dtcmBarcode", ""] },
              }
            : {}),
          id: { $toString: "$_id" },
          order: 1,
          "Order Created By": "$order.transition.createdBy",
        },
      },
      {
        $replaceRoot: {
          newRoot: {
            $mergeObjects: [
              "$$ROOT",
              "$orderDynamicField",
              "$ticketDynamicField",
            ],
          },
        },
      },
      {
        $addFields: {
          id: { $toString: "$orderItemId" },
        },
      },
      {
        $project: {
          orderDynamicField: 0,
          ticketDynamicField: 0,
        },
      },
    ];

    const reverseKeyset =
      paginate &&
      limit > 0 &&
      Boolean(beforeCursor && isValidObjectId(beforeCursor));

    const forwardKeyset =
      paginate &&
      limit > 0 &&
      !reverseKeyset &&
      Boolean(cursorId && isValidObjectId(cursorId));

    const pageLimit = paginate && limit > 0 ? limit + 1 : limit;

    const resultFacetPaginationStages: any[] = reverseKeyset
      ? [
          { $match: { _id: { $gt: { $oid: beforeCursor as string } } } },
          { $sort: { _id: 1 } },
          { $limit: pageLimit },
          { $sort: { _id: -1 } },
        ]
      : forwardKeyset
      ? [
          { $match: { _id: { $lt: { $oid: cursorId as string } } } },
          { $sort: { _id: -1 } },
          { $limit: pageLimit },
        ]
      : [
          { $sort: { _id: -1 } },
          ...(paginate && limit > 0 ? [{ $limit: pageLimit }] : []),
        ];

    const footerStages = [
      {
        $facet: {
          stats: [
            {
              $group: {
                _id: null,
                total: { $sum: 1 },
                checkedIn: {
                  $sum: {
                    $cond: [
                      { $eq: ["$Status", CheckInOutType.CHECK_IN] },
                      1,
                      0,
                    ],
                  },
                },
                checkedOut: {
                  $sum: {
                    $cond: [
                      { $eq: ["$Status", CheckInOutType.CHECK_OUT] },
                      1,
                      0,
                    ],
                  },
                },
                notCheckedIn: {
                  $sum: {
                    $cond: [
                      { $eq: ["$Status", "NOT_CHECK_IN"] },
                      1,
                      0,
                    ],
                  },
                },
              },
            },
          ],
          pagination: [
            {
              $group: {
                _id: null,
                total: { $sum: 1 },
              },
            },
            {
              $addFields: {
                hasNextPage: {
                  $literal: false,
                },
                hasPreviousPage: {
                  $literal: false,
                },
              },
            },
          ],
          result: [
            ...resultFacetPaginationStages,
            ...(projectStage.length ? projectStage : baseProjectStage),
            ...(deselectedColumns
              ? [
                  {
                    $project: {
                      OrderID: 0,
                      dynamicField: 0,
                      formResultEntity: 0,
                      invitation: 0,
                      ticket: 0,
                      orderFormResult: 0,
                      order: 0,
                      contact: 0,
                      ...deselectedColumns,
                    },
                  },
                ]
              : []),
          ],
        },
      },
    ];
    // Always start from Order (indexed eventId + COMPLETED). `ids` is just an
    // OrderItem `_id` filter inside the Order → OrderItem lookup — same as ticketId / badge.
    const orderItemRootFilterConditions: MongoPipelineStage[] = [];
    const validRootOrderItemIds =
      ids && ids.length > 0 && !group
        ? ids.filter(
            (orderItemId) => orderItemId && isValidObjectId(orderItemId)
          )
        : [];

    if (validRootOrderItemIds.length > 0) {
      orderItemRootFilterConditions.push({
        _id: { $in: validRootOrderItemIds.map((oid) => ({ $oid: oid })) },
      });
    }
    if (ticketId && isValidObjectId(ticketId)) {
      orderItemRootFilterConditions.push({ ticketId: { $oid: ticketId } });
    }
    if (badgeStatus === "printed") {
      orderItemRootFilterConditions.push({ printed: { $eq: true } });
    } else if (badgeStatus === "not_printed") {
      orderItemRootFilterConditions.push({ printed: { $ne: true } });
    }
    if (entityTierId && entityTierId !== "" && isValidObjectId(entityTierId)) {
      orderItemRootFilterConditions.push({
        entityTierId: { $oid: entityTierId },
      });
    }

    if (!scopeEntityId) {
      return buildEmptyAttendeeReportAggregationResponse(limit);
    }

    const orderScopeMatch = await resolveCompletedOrderScopeMatch(
      scopeEntityId,
      type,
      prisma
    );

    if (!orderScopeMatch) {
      return buildEmptyAttendeeReportAggregationResponse(limit);
    }

    // When specific ids were requested but none are valid, nothing to enrich.
    if (
      ids &&
      ids.length > 0 &&
      !group &&
      validRootOrderItemIds.length === 0
    ) {
      return buildEmptyAttendeeReportAggregationResponse(limit);
    }

    const orderRootOpeningStages: MongoPipelineStage[] = [
      orderScopeMatch,
      ...buildOrderRootToOrderItemStages(orderItemRootFilterConditions),
    ];

    const stages: any[] = [
      ...orderRootOpeningStages,
      {
        $lookup: {
          from: "Transition",
          let: { orderId: "$order._id" },
          pipeline: [
            {
              $match: {
                $expr: { $eq: ["$transitionalId", "$$orderId"] },
                toState: TransitionState.COMPLETED,
              },
            },
            { $sort: { createdAt: -1 } }, // newest first
            { $limit: 1 }, // keep only the last one
            { $project: { createdBy: 1 } },
          ],
          as: "order.transition",
        },
      },
      {
        $unwind: {
          path: "$order.transition",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "Event",
          localField: "order.eventId",
          foreignField: "_id",
          as: "event",
        },
      },
      { $unwind: { path: "$event", preserveNullAndEmptyArrays: true } },
      {
        $lookup: {
          from: "FormResultEntity",
          let: { orderId: "$order._id" },
          pipeline: [
            {
              $match: {
                $expr: {
                  $and: [
                    { $eq: ["$orderId", "$$orderId"] },
                    { $eq: ["$type", "ORDER"] },
                  ],
                },
              },
            },
            { $limit: 1 },
          ],
          as: "orderFormResultEntity",
        },
      },
      {
        $unwind: {
          path: "$orderFormResultEntity",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "FormResult",
          localField: "orderFormResultEntity.formResultId",
          foreignField: "_id",
          as: "orderFormResultEntity.formResult",
        },
      },
      {
        $unwind: {
          path: "$orderFormResultEntity.formResult",
          preserveNullAndEmptyArrays: true,
        },
      },

      {
        $lookup: {
          from: "FormResultEntity",
          let: { orderItemId: "$_id" },
          pipeline: [
            {
              $match: {
                $expr: { $eq: ["$orderItemId", "$$orderItemId"] },
              },
            },
            { $limit: 1 },
          ],
          as: "formResultEntity",
        },
      },
      {
        $unwind: {
          path: "$formResultEntity",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "FormResult",
          localField: "formResultEntity.formResultId",
          foreignField: "_id",
          as: "formResultEntity.formResult",
        },
      },
      {
        $unwind: {
          path: "$formResultEntity.formResult",
          preserveNullAndEmptyArrays: true,
        },
      },

      {
        $lookup: {
          from: "InvitationAttendee",
          localField: "invitationAttendeeId",
          foreignField: "_id",
          as: "invitationAttendee",
        },
      },
      {
        $unwind: {
          path: "$invitationAttendee",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "Invitation",
          localField: "invitationAttendee.invitationId",
          foreignField: "_id",
          as: "invitation",
        },
      },
      {
        $unwind: {
          path: "$invitation",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "InvitationTranslations",
          localField: "invitation._id",
          foreignField: "invitationId",
          as: "invitation.translations",
          pipeline: translationLookupPipeline(languageOid),
        },
      },
      {
        $lookup: {
          from: "Profile",
          localField: "invitationAttendee.contactId",
          foreignField: "_id",
          as: "contact",
        },
      },
      {
        $unwind: {
          path: "$contact",
          preserveNullAndEmptyArrays: true,
        },
      },
      // Also look up the order buyer's profile (for non-invitation orders)
      {
        $lookup: {
          from: "Profile",
          localField: "order.profileId",
          foreignField: "_id",
          as: "orderProfile",
        },
      },
      {
        $unwind: {
          path: "$orderProfile",
          preserveNullAndEmptyArrays: true,
        },
      },
      // Merge: invitation contact takes priority, order profile is fallback
      {
        $addFields: {
          contact: {
            $ifNull: ["$contact", "$orderProfile"],
          },
        },
      },
      {
        $addFields: {
          buyerEmail: {
            $let: {
              vars: {
                emailField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray: {
                            $ifNull: [
                              "$formResultEntity.formResult.surveyResult",
                              {},
                            ],
                          },
                        },
                        as: "result",
                        cond: { $eq: ["$$result.k", "email"] },
                      },
                    },
                    0,
                  ],
                },
                orderEmailField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray: {
                            $ifNull: [
                              "$orderFormResultEntity.formResult.surveyResult",
                              {},
                            ],
                          },
                        },
                        as: "result",
                        cond: { $eq: ["$$result.k", "email"] },
                      },
                    },
                    0,
                  ],
                },
              },
              in: {
                $cond: {
                  if: { $ne: [{ $ifNull: ["$$emailField.v", ""] }, ""] },
                  then: "$$emailField.v",
                  else: {
                    $cond: {
                      if: {
                        $ne: [{ $ifNull: ["$$orderEmailField.v", ""] }, ""],
                      },
                      then: "$$orderEmailField.v",
                      else: { $ifNull: ["$contact.email", ""] },
                    },
                  },
                },
              },
            },
          },
          buyerName: {
            $let: {
              vars: {
                nameField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray: {
                            $ifNull: [
                              "$formResultEntity.formResult.surveyResult",
                              {},
                            ],
                          },
                        },
                        as: "result",
                        cond: { $eq: ["$$result.k", "name"] },
                      },
                    },
                    0,
                  ],
                },
                orderNameField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray: {
                            $ifNull: [
                              "$orderFormResultEntity.formResult.surveyResult",
                              {},
                            ],
                          },
                        },
                        as: "result",
                        cond: { $eq: ["$$result.k", "name"] },
                      },
                    },
                    0,
                  ],
                },
              },
              in: {
                $cond: {
                  if: { $ne: [{ $ifNull: ["$$nameField.v", ""] }, ""] },
                  then: "$$nameField.v",
                  else: {
                    $cond: {
                      if: {
                        $ne: [{ $ifNull: ["$$orderNameField.v", ""] }, ""],
                      },
                      then: "$$orderNameField.v",
                      else: { $ifNull: ["$contact.name", ""] },
                    },
                  },
                },
              },
            },
          },
          Phone: {
            $let: {
              vars: {
                phoneField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray: {
                            $ifNull: [
                              "$formResultEntity.formResult.surveyResult",
                              {},
                            ],
                          },
                        },
                        as: "result",
                        cond: { $eq: ["$$result.k", "phone"] },
                      },
                    },
                    0,
                  ],
                },
              },
              in: {
                $cond: {
                  if: { $ne: [{ $ifNull: ["$$phoneField.v", ""] }, ""] },
                  then: "$$phoneField.v",
                  else: { $ifNull: ["$contact.phoneNumber", ""] },
                },
              },
            },
          },
        },
      },
      {
        $lookup: {
          from: "EventTranslations",
          localField: "event._id",
          foreignField: "eventId",
          as: "event.eventTranslations",
          pipeline: translationLookupPipeline(languageOid),
        },
      },
      {
        $lookup: {
          from: "Ticket",
          localField: "ticketId",
          foreignField: "_id",
          as: "ticket",
        },
      },
      { $unwind: { path: "$ticket", preserveNullAndEmptyArrays: true } },
      // Do not filter out items; just mark whether this ticket is accessible by the agent zones
      ...(userId
        ? [
            {
              $addFields: {
                isTicketAccessibleByZones: {
                  $in: ["$ticket._id", accessibleTicketOids],
                },
              },
            },
          ]
        : [
            {
              $addFields: {
                isTicketAccessibleByZones: true,
              },
            },
          ]),

      ...(isDTCMEvent
        ? [
            {
              $lookup: {
                from: "DTCMTicketPrice",
                localField: "ticket.dtcmTicketPriceId",
                foreignField: "_id",
                as: "ticket.dtcmTicketPrice",
              },
            },
            {
              $unwind: {
                path: "$ticket.dtcmTicketPrice",
                preserveNullAndEmptyArrays: true,
              },
            },
            {
              $lookup: {
                from: "DTCMPriceCategory",
                localField: "ticket.dtcmTicketPrice.categoryId",
                foreignField: "_id",
                as: "ticket.dtcmTicketPrice.category",
              },
            },
            {
              $unwind: {
                path: "$ticket.dtcmTicketPrice.category",
                preserveNullAndEmptyArrays: true,
              },
            },
          ]
        : []),
      {
        $lookup: {
          from: "BookableItemTranslations",
          localField: "ticket.bookableItemId",
          foreignField: "bookableItemId",
          as: "ticket.translations",
          pipeline: translationLookupPipeline(languageOid),
        },
      },
      {
        $lookup: {
          from: "TicketCategory",
          localField: "ticket.categoryId",
          foreignField: "_id",
          as: "category",
        },
      },
      {
        $unwind: { path: "$category", preserveNullAndEmptyArrays: true },
      },
      {
        $lookup: {
          from: "CheckInOut",
          let: { orderItemId: "$_id" },
          pipeline: [
            {
              $match: {
                $expr: {
                  $eq: ["$orderItemId", "$$orderItemId"],
                },
              },
            },
            {
              $sort: { time: 1, _id: 1 },
            },
            {
              $project: {
                type: 1,
                time: 1,
                createdAt: 1,
                updatedAt: 1,
                deletedAt: 1,
                createdById: 1,
                orderItemId: 1,
                zoneId: 1,
                autoCheckOut: 1,
                from: 1,
              },
            },
          ],
          as: "checksInOut",
        },
      },
      ...(attendee && attendee !== "all"
        ? attendee === NOT_CHECKED_IN
          ? [
              {
                $match: {
                  $expr: {
                    $eq: [{ $size: { $ifNull: ["$checksInOut", []] } }, 0],
                  },
                },
              },
            ]
          : [
              {
                $match: {
                  $expr: {
                    $eq: [
                      { $arrayElemAt: ["$checksInOut.type", -1] },
                      attendee,
                    ],
                  },
                },
              },
            ]
        : []),
      ...(zone && zone !== ""
        ? [
            {
              $match: {
                checksInOut: {
                  $elemMatch: { zoneId: { $oid: zone } },
                },
              },
            },
          ]
        : []),
      ...(zoneId && zoneId !== ""
        ? [
            {
              $match: {
                checksInOut: {
                  $elemMatch: { zoneId: { $oid: zoneId } },
                },
              },
            },
          ]
        : []),
      ...(categoryId && categoryId !== ""
        ? [
            {
              $match: {
                $expr: {
                  $eq: ["$ticket.categoryId", { $oid: categoryId }],
                },
              },
            },
          ]
        : []),
      {
        $addFields: {
          firstCheckIn: {
            $arrayElemAt: [
              {
                $filter: {
                  input: "$checksInOut",
                  as: "ci",
                  cond: {
                    $and: [
                      { $eq: ["$$ci.type", "CHECK_IN"] },
                      {
                        $or: [
                          { $eq: ["$$ci.deletedAt", null] },
                          { $not: [{ $ifNull: ["$$ci.deletedAt", false] }] },
                        ],
                      },
                    ],
                  },
                },
              },
              0, // First check-in
            ],
          },
          lastCheckIn: {
            $arrayElemAt: [
              {
                $filter: {
                  input: "$checksInOut",
                  as: "ci",
                  cond: {
                    $and: [
                      { $eq: ["$$ci.type", "CHECK_IN"] },
                      {
                        $or: [
                          { $eq: ["$$ci.deletedAt", null] },
                          { $not: [{ $ifNull: ["$$ci.deletedAt", false] }] },
                        ],
                      },
                    ],
                  },
                },
              },
              -1, // Last check-in (checksInOut is sorted by time asc)
            ],
          },
          lastCheckOut: {
            $arrayElemAt: [
              {
                $filter: {
                  input: "$checksInOut",
                  as: "ci",
                  cond: {
                    $and: [
                      { $eq: ["$$ci.type", "CHECK_OUT"] },
                      {
                        $or: [
                          { $eq: ["$$ci.deletedAt", null] },
                          { $not: [{ $ifNull: ["$$ci.deletedAt", false] }] },
                        ],
                      },
                    ],
                  },
                },
              },
              -1,
            ],
          },
          // Calculate the last type (either last check-in or last check-out)
          Status: {
            $ifNull: [
              { $arrayElemAt: ["$checksInOut.type", -1] },
              "NOT_CHECK_IN",
            ],
          },
        },
      },
      {
        $addFields: {
          firstCheckInDate: "$firstCheckIn.time",
          lastCheckOutDate: "$lastCheckOut.time",
          "Time Spent": {
            $cond: {
              if: {
                $and: [
                  { $ifNull: ["$firstCheckIn.time", false] },
                  { $ifNull: ["$lastCheckOut.time", false] },
                ],
              },
              then: {
                $let: {
                  vars: {
                    diffMillis: {
                      $subtract: [
                        { $toDate: "$lastCheckOut.time" },
                        { $toDate: "$firstCheckIn.time" },
                      ],
                    },
                  },
                  in: {
                    $concat: [
                      {
                        $toString: {
                          $floor: { $divide: ["$$diffMillis", 3600000] },
                        },
                      }, // Hours
                      ":",
                      {
                        $toString: {
                          $cond: {
                            if: {
                              $lt: [
                                {
                                  $mod: [
                                    {
                                      $floor: {
                                        $divide: ["$$diffMillis", 60000],
                                      },
                                    },
                                    60,
                                  ],
                                },
                                10,
                              ],
                            },
                            then: {
                              $concat: [
                                "0",
                                {
                                  $toString: {
                                    $mod: [
                                      {
                                        $floor: {
                                          $divide: ["$$diffMillis", 60000],
                                        },
                                      },
                                      60,
                                    ],
                                  },
                                },
                              ],
                            },
                            else: {
                              $toString: {
                                $mod: [
                                  {
                                    $floor: {
                                      $divide: ["$$diffMillis", 60000],
                                    },
                                  },
                                  60,
                                ],
                              },
                            },
                          },
                        },
                      }, // Minutes
                      ":",
                      {
                        $toString: {
                          $cond: {
                            if: {
                              $lt: [
                                {
                                  $mod: [
                                    {
                                      $floor: {
                                        $divide: ["$$diffMillis", 1000],
                                      },
                                    },
                                    60,
                                  ],
                                },
                                10,
                              ],
                            },
                            then: {
                              $concat: [
                                "0",
                                {
                                  $toString: {
                                    $mod: [
                                      {
                                        $floor: {
                                          $divide: ["$$diffMillis", 1000],
                                        },
                                      },
                                      60,
                                    ],
                                  },
                                },
                              ],
                            },
                            else: {
                              $toString: {
                                $mod: [
                                  {
                                    $floor: {
                                      $divide: ["$$diffMillis", 1000],
                                    },
                                  },
                                  60,
                                ],
                              },
                            },
                          },
                        },
                      }, // Seconds
                    ],
                  },
                },
              },
              else: {
                $cond: {
                  if: { $ifNull: ["$firstCheckIn.time", false] },
                  then: {
                    $let: {
                      vars: {
                        diffMillis: {
                          $subtract: [
                            "$$NOW",
                            { $toDate: "$firstCheckIn.time" },
                          ],
                        },
                      },
                      in: {
                        $concat: [
                          {
                            $toString: {
                              $floor: { $divide: ["$$diffMillis", 3600000] },
                            },
                          }, // Hours
                          ":",
                          {
                            $toString: {
                              $cond: {
                                if: {
                                  $lt: [
                                    {
                                      $mod: [
                                        {
                                          $floor: {
                                            $divide: ["$$diffMillis", 60000],
                                          },
                                        },
                                        60,
                                      ],
                                    },
                                    10,
                                  ],
                                },
                                then: {
                                  $concat: [
                                    "0",
                                    {
                                      $toString: {
                                        $mod: [
                                          {
                                            $floor: {
                                              $divide: ["$$diffMillis", 60000],
                                            },
                                          },
                                          60,
                                        ],
                                      },
                                    },
                                  ],
                                },
                                else: {
                                  $toString: {
                                    $mod: [
                                      {
                                        $floor: {
                                          $divide: ["$$diffMillis", 60000],
                                        },
                                      },
                                      60,
                                    ],
                                  },
                                },
                              },
                            },
                          }, // Minutes
                          ":",
                          {
                            $toString: {
                              $cond: {
                                if: {
                                  $lt: [
                                    {
                                      $mod: [
                                        {
                                          $floor: {
                                            $divide: ["$$diffMillis", 1000],
                                          },
                                        },
                                        60,
                                      ],
                                    },
                                    10,
                                  ],
                                },
                                then: {
                                  $concat: [
                                    "0",
                                    {
                                      $toString: {
                                        $mod: [
                                          {
                                            $floor: {
                                              $divide: ["$$diffMillis", 1000],
                                            },
                                          },
                                          60,
                                        ],
                                      },
                                    },
                                  ],
                                },
                                else: {
                                  $toString: {
                                    $mod: [
                                      {
                                        $floor: {
                                          $divide: ["$$diffMillis", 1000],
                                        },
                                      },
                                      60,
                                    ],
                                  },
                                },
                              },
                            },
                          }, // Seconds
                        ],
                      },
                    },
                  },
                  else: "-", // Return "-" if neither checkIn nor checkOut exists
                },
              },
            },
          },
        },
      },
      {
        // Deduplicate agent IDs first — equality $lookup on an array localField
        // emits one User doc per array element, so repeated createdById values
        // would duplicate agents without this step.
        $addFields: {
          distinctAgentIds: {
            $setUnion: [{ $ifNull: ["$checksInOut.createdById", []] }, []],
          },
        },
      },
      {
        // Equality lookup on `_id` — uses User._id index (avoids `$expr` COLLSCAN).
        $lookup: {
          from: "User",
          localField: "distinctAgentIds",
          foreignField: "_id",
          as: "agents",
        },
      },
      {
        $addFields: {
          uniqueAgents: {
            $map: {
              input: "$agents",
              as: "a",
              in: { id: "$$a._id", name: "$$a.name" },
            },
          },
        },
      },
      {
        $addFields: {
          orderDynamicField: {
            $ifNull: ["$orderFormResultEntity.formResult.surveyResult", {}],
          },
          ticketDynamicField: {
            $ifNull: ["$formResultEntity.formResult.surveyResult", {}],
          },
          "ticket.id": { $toString: "$ticket._id" },
          "invitation.id": { $toString: "$invitation._id" },
        },
      },
      ...filtrationStages,
      ...groupByEventStage,
      ...footerStages,
    ];
    const result = (await prisma.order.aggregateRaw({
      pipeline: [...stages],
    })) as unknown as AttendeeReportAggregationRawResponse[];
    const response = result[0];
    if (response?.pagination?.[0]) {
      const fetchedRows = Array.isArray(response.result) ? response.result : [];
      const fetchedExtra = paginate && limit > 0 && fetchedRows.length > limit;
      response.pagination[0].limit = limit;
      response.pagination[0].hasPreviousPage = reverseKeyset
        ? fetchedExtra
        : forwardKeyset;
      response.pagination[0].hasNextPage = reverseKeyset ? true : fetchedExtra;
      if (paginate && limit > 0) {
        response.result =
          reverseKeyset && fetchedExtra
            ? fetchedRows.slice(1)
            : fetchedRows.slice(0, limit);
      }
    }

    return response ?? buildEmptyAttendeeReportAggregationResponse(limit);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getAttendeeReportFiltersData = async (
  entityId: string,
  entityType: REPORT_TYPE
) => {
  const prisma = reportingPrisma;
  let orderCondition: any = { status: OrderStatus.COMPLETED };
  if (entityType === REPORT_TYPE.WHITE_LABEL) {
    orderCondition.event = { whiteLabelId: entityId };
  } else {
    orderCondition.eventId = entityId;
  }

  const [distinctTicketItems, checkInOuts] = await Promise.all([
    // 1. Distinct tickets from OrderItems scoped via Order relation
    prisma.orderItem.findMany({
      where: {
        order: orderCondition,
        ticketId: { not: null },
      },
      select: {
        ticketId: true,
        ticket: {
          select: {
            id: true,
            bookableItem: {
              select: {
                translations: { select: { name: true } },
              },
            },
          },
        },
      },
      distinct: ["ticketId"],
    }),
    // 2. Distinct agents from CheckInOuts
    prisma.checkInOut.findMany({
      where: {
        orderItem: { order: orderCondition },
      },
      select: { createdById: true },
      distinct: ["createdById"],
    }),
  ]);

  // Build tickets list
  const tickets = distinctTicketItems
    .filter((item) => item.ticket)
    .map((item) => {
      const translations = item.ticket?.bookableItem?.translations ?? [];
      const name = translations.find((tr) => tr.name)?.name ?? item.ticketId;
      return { id: item.ticketId!, name };
    });

  // Build agents list
  const agentIds = checkInOuts.map((c) => c.createdById);
  const users = agentIds.length
    ? await prisma.user.findMany({
        where: { id: { in: agentIds } },
        select: { id: true, name: true },
      })
    : [];
  const agents = users.map((u) => ({ id: u.id, name: u.name }));

  return { tickets, agents };
};

export type AttendeeReportFilterParams = {
  zone?: string;
  search?: string;
  attendee?: string;
  group?: string;
};

export async function resolveAttendeeReportOrderItemIds(
  eventId: string,
  searchParams?: AttendeeReportFilterParams
): Promise<string[]> {
  const report = await attendeeReportAggregation(
    eventId,
    -1,
    searchParams ?? {},
    REPORT_TYPE.EVENT,
    false,
    "en",
    {},
    true
  );

  const rows = Array.isArray(report?.result) ? report.result : [];
  return rows
    .map((row: { id?: string }) => row.id)
    .filter((id): id is string => typeof id === "string" && id.length > 0);
}
